import React, { useState, useEffect } from 'react';
import { userApi, paymentApi, productApi } from '../services/api';
import { QRCodeCanvas } from 'qrcode.react';

function SubscriptionManager({ moduleId, onClose, onSubscriptionSuccess, onLoginRequired }) {
  const [subscriptions, setSubscriptions] = useState({
    suck: false,
    wash: false,
    rise: false,
    sell: false
  });
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, success, failed
  const [showQRCode, setShowQRCode] = useState(false);

  // 模块名称映射
  const moduleNames = {
    suck: '吸筹阶段',
    wash: '洗盘阶段',
    rise: '拉升阶段',
    sell: '出货阶段'
  };

  useEffect(() => {
    // 获取用户订阅信息和产品列表
    const fetchData = async () => {
      setLoading(true);
      setError('');
      
      try {
        // 获取用户订阅信息
        const subscriptionsResponse = await userApi.getUserSubscriptions();
        if (subscriptionsResponse.success) {
          setSubscriptions(subscriptionsResponse.data);
        }
        
        // 获取产品列表
        const productsResponse = await productApi.getProducts(moduleId);
        if (productsResponse.success) {
          console.log('获取到产品列表:', productsResponse.data);
          setProducts(productsResponse.data);
          // 默认选中第一个产品
          if (productsResponse.data.length > 0) {
            setSelectedProduct(productsResponse.data[0]);
          }
        } else {
          console.error('获取产品列表失败:', productsResponse.error);
        }
      } catch (err) {
        setError('获取数据失败，请稍后再试');
        console.error('Fetch data error:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [moduleId]);

  // 处理产品选择
  const handleProductSelect = (product) => {
    console.log('选择产品:', product);
    setSelectedProduct(product);
  };

  // 处理支付：先判断是否登录，如果没有登录则弹出登录提示框
  const handlePayment = async () => {
    // 登录状态检查：如果 token 不存在，则提示登录
    const token = localStorage.getItem('token');
    if (!token) {
      if (onLoginRequired) {
        onLoginRequired();
      } else {
        alert('请先登录');
      }
      return;
    }

    console.log('点击支付按钮, 选中产品:', selectedProduct);
    
    if (!selectedProduct) {
      setError('请选择一个订阅计划');
      return;
    }
    
    setPaymentLoading(true);
    setError('');
    
    try {
      // 确保产品数据格式正确
      const productForPayment = {
        id: selectedProduct.id,
        name: selectedProduct.name,
        price: selectedProduct.price,
        period: selectedProduct.period || 'monthly'
      };
      
      console.log('创建订单, 参数:', productForPayment);
      const response = await paymentApi.createOrder(productForPayment);
      console.log('创建订单响应:', response);
      
      if (response.success) {
        const orderId = response.data.sessionId;
        setPaymentInfo(response.data);
        setShowQRCode(true);
        await queryOrderStatus(orderId);
      } else {
        setError(response.error || '创建订单失败，请稍后再试');
        console.error('创建订单失败:', response.error);
      }
    } catch (error) {
      console.error('Error during payment:', error);
      alert('支付过程中发生错误，请稍后重试。');
    } finally {
      setPaymentLoading(false);
    }
  };

  const queryOrderStatus = async (orderId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await paymentApi.queryOrderStatus(orderId, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.success) {
        console.log('支付成功');
        setPaymentStatus('success');
        
        // 更新订阅状态
        const newSubscriptions = { ...subscriptions };
        newSubscriptions[moduleId] = true;
        setSubscriptions(newSubscriptions);
        
        // 3秒后关闭支付页面，回到内容页面
        setTimeout(() => {
          console.log('支付成功, 关闭支付页面');
          setShowQRCode(false);
          if (onSubscriptionSuccess) {
            onSubscriptionSuccess();
          } else {
            onClose();
          }
        }, 3000);
      } else {
        console.error('Order status error:', response.error);
        setPaymentStatus('failed');
      }
    } catch (error) {
      console.error('Error querying order status:', error);
      setPaymentStatus('failed');
    }
  };

  // 关闭支付二维码
  const handleCloseQRCode = () => {
    setShowQRCode(false);
  };

  // 检查订阅状态
  const isSubscribed = subscriptions[moduleId];

  if (loading) {
    return (
      <div className="subscription-manager">
        <div className="loading">加载中...</div>
      </div>
    );
  }

  // 支付二维码弹窗
  if (showQRCode && paymentInfo) {
    return (
      <div id="paymentSection" style={{ display: 'block' }}>
        <h3>请扫码支付</h3>
        {paymentInfo.url ? (
          <div id="qrcode">
            <QRCodeCanvas value={paymentInfo.url} size={200} />
          </div>
        ) : (
          <div>二维码生成失败，请稍后重试。</div>
        )}
        <p>完成支付后系统将自动更新状态</p>
        {paymentStatus === 'pending' && <div className="payment-status">处理中，请稍候...</div>}
        {paymentStatus === 'success' && <div className="payment-success">支付成功！</div>}
        {paymentStatus === 'failed' && <div className="payment-failed">支付失败</div>}
        <button id="closePaymentBtn" onClick={handleCloseQRCode}>关闭</button>
      </div>
    );
  }

  return (
    <div className="subscription-manager">
      <h2>{moduleNames[moduleId] || '模块'} 订阅</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      {isSubscribed ? (
        <div className="subscription-status">
          <div className="status-badge subscribed">已订阅</div>
          <p>您已订阅 {moduleNames[moduleId]} 模块，可以查看完整内容。</p>
        </div>
      ) : (
        <div className="membership-section">
          <h2 className="membership-title">选择会员套餐</h2>
          <div className="membership-cards">
            {products.length > 0 ? (
              products.map(product => (
                <div 
                  key={product.id}
                  className={`membership-card ${selectedProduct?.id === product.id ? 'featured' : ''}`}
                  onClick={() => handleProductSelect(product)}
                >
                  <div className="card-header">{product.name}</div>
                  <div className="card-price">¥{product.price} <span className="card-period">/{product.period === 'monthly' ? '月' : '年'}</span></div>
                  <div className="card-divider"></div>
                  <div className="card-benefits">
                    <ul>
                      <li>AI推荐股票</li>
                      <li>详细操作指南</li>
                      <li>实时市场分析</li>
                      {product.features && product.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                  <a
                                  href="#"
                                  className="card-button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation(); // 阻止事件冒泡
                                    handlePayment();
                                  }}
                                >
                                  {paymentLoading ? '处理中...' : '购买'}
                                </a>

                </div>
              ))
            ) : (
              <div className="no-products">暂无可用订阅计划</div>
            )}
          </div>
        </div>
      )}
      
      {/* <button className="close-button" onClick={onClose}>关闭</button> */}
    </div>
  );
}

export default SubscriptionManager;
